import {Box, Grid, Paper, Typography, CircularProgress, Chip} from '@mui/material';
import {Person, EmojiEvents} from '@mui/icons-material';
import QuizPlayerList from './QuizPlayerList';
import '../../styles/components/presenter-view.css';

const PresenterView = ({
  quiz,
  gameState,
  timeLeft,
  players,
  currentUser,
  currentQuestionIdx,
  animatingScores,
  showAnswers,
  isPaused
}) => {
  // Aktuelle Frage und Antworten
  const currentQuestion = gameState.currentQuestion;
  const questionIndex = gameState.currentQuestionIdx;
  const totalQuestions = quiz?.questions?.length || 0;
  
  // Timer-Farbe basierend auf verbleibender Zeit
  const getTimerColor = () => {
    if (timeLeft > 30) return '#4caf50';
    if (timeLeft > 10) return '#ff9800';
    return '#f44336';
  };

  // Bestimme die korrekten Antworten
  const getAnswerStatus = (option, index) => {
    const currentQuestionData = quiz.questions[questionIndex];
    let isCorrect = false;
    
    if (currentQuestionData.correctAnswer) {
      isCorrect = option === currentQuestionData.correctAnswer;
    } else if (Array.isArray(currentQuestionData.correctAnswers)) {
      isCorrect = currentQuestionData.correctAnswers.includes(index);
    }
    
    return {
      isCorrect,
      backgroundColor: showAnswers 
        ? (isCorrect 
            ? 'linear-gradient(135deg, rgba(46, 125, 50, 0.95) 0%, rgba(67, 160, 71, 0.95) 100%)'
            : 'rgba(28, 39, 60, 0.6)')
        : 'rgba(28, 39, 60, 0.6)',
      borderColor: showAnswers && isCorrect ? 'rgba(76, 175, 80, 0.5)' : 'rgba(255, 255, 255, 0.1)'
    };
  };

  return (
    <Box className="presenter-view">
      {/* Header mit Quiz-Titel und Timer */}
      <Paper 
        className="glass"
        sx={{ 
          p: 3, 
          mb: 3,
          background: 'rgba(28, 39, 60, 0.4)',
          backdropFilter: 'blur(10px)',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Typography 
            variant="h4"
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, var(--color-secondary) 0%, var(--color-secondary-light) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            {quiz.title}
          </Typography>
          <Typography 
            variant="h6"
            sx={{
              color: 'var(--color-text-secondary)',
              mt: 1
            }}
          >
            Frage {questionIndex + 1} von {totalQuestions}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isPaused && (
            <Chip 
              label="PAUSIERT" 
              color="warning" 
              sx={{ 
                fontSize: '1.2rem', 
                fontWeight: 'bold',
                py: 2,
                px: 1
              }} 
            />
          )}
          
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={(timeLeft / 60) * 100}
              size={100}
              thickness={5}
              sx={{
                color: getTimerColor(),
                transition: 'all 0.3s ease',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                  filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.3))'
                }
              }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography 
                variant="h4" 
                component="div" 
                sx={{ 
                  color: 'var(--color-text-primary)',
                  fontWeight: 'bold',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                }}
              >
                {timeLeft}s
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Frage und Antworten */}
        <Grid item xs={12} md={8}>
          <Paper 
            className="glass"
            sx={{ 
              p: 4,
              background: 'rgba(28, 39, 60, 0.4)',
              backdropFilter: 'blur(10px)',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{
                fontWeight: 'bold',
                color: 'var(--color-text-primary)',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                mb: 4
              }}
            >
              {currentQuestion.text}
            </Typography>
            
            <Grid container spacing={2}>
              {currentQuestion.options?.map((option, index) => {
                const { isCorrect, backgroundColor, borderColor } = getAnswerStatus(option, index);
                
                return (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box
                      sx={{
                        minHeight: 80,
                        p: 2,
                        background: backgroundColor,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid',
                        borderColor: borderColor,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        animation: showAnswers && isCorrect ? 'pulse 2s infinite' : 'none'
                      }}
                    >
                      <Typography 
                        variant="h6"
                        sx={{
                          color: 'var(--color-text-primary)',
                          fontWeight: showAnswers && isCorrect ? 'bold' : 'normal',
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                          textAlign: 'center'
                        }}
                      >
                        {option}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>

        {/* Spieler-Status */}
        <Grid item xs={12} md={4}>
          <Paper 
            className="glass"
            sx={{ 
              p: 3,
              background: 'rgba(28, 39, 60, 0.4)',
              backdropFilter: 'blur(10px)',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <EmojiEvents sx={{ color: 'var(--color-secondary)', fontSize: '2rem' }} />
              <Typography 
                variant="h5" 
                sx={{
                  fontWeight: 'bold',
                  color: 'var(--color-secondary)'
                }}
              >
                Rangliste
              </Typography>
              <Chip 
                icon={<Person />} 
                label={`${players.length} Spieler`} 
                size="small"
                sx={{ ml: 'auto' }}
              />
            </Box>
            
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <QuizPlayerList
                players={players}
                currentUser={currentUser}
                currentQuestionIdx={currentQuestionIdx}
                animatingScores={animatingScores}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PresenterView; 